import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import {
	ButtonStandard,
	Checkbox,
	InputText,
	InputWrapper,
	PYColumnDef,
	PageBanner,
	Table,
	TableRef,
	Text,
	Toggle,
	toast,
} from "@performyard/py-components";

import { useEngagementSurveyQuestions } from "../../../../../../api/hooks/defs/engagement";
import { useEngagementCustomFactors } from "../../../../../../api/hooks/defs/engagement-custom-factors";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

import { scaleTypeMap } from "../../../../../../api/services";

interface ManuallySelectQuestionsProps {
	onBack: () => void;
	onSelectQuestions: (questions: string[]) => void;
	initialSelectedQuestionIds?: string[];
}

type ManualQuestionRow = {
	questionId: string;
	factor: string;
	question: string;
	typeLabel: string;
};

const MAX_SELECTED_QUESTIONS = 32;

const ManuallySelectQuestions = ({
	onBack,
	onSelectQuestions,
	initialSelectedQuestionIds = [],
}: ManuallySelectQuestionsProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
	const [showOnlySelectedQuestions, setShowOnlySelectedQuestions] = useState(false);
	const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>(initialSelectedQuestionIds);

	const gridRef = useRef<TableRef<ManualQuestionRow>>(null);

	const { data, isLoading } = useEngagementSurveyQuestions();

	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", false);
	const { data: customFactorsData } = useEngagementCustomFactors({ enabled: customFactorsEnabled });
	const customFactorsMap = useMemo(() => {
		const map: Record<string, string> = {};
		if (customFactorsData?.customFactors) {
			for (const f of customFactorsData.customFactors) {
				map[f._id] = f.name;
			}
		}
		return map;
	}, [customFactorsData?.customFactors]);

	const questions = (data?.results ?? [])?.filter(
		(question) => question.enabled && !question.deleted_at && !["eNPS", "Other"].includes(question.factor)
	);

	const questionRows = useMemo<ManualQuestionRow[]>(() => {
		return questions
			.map((question) => {
				const questionId = question?._id ?? question?.id ?? null;

				if (!questionId) {
					return null;
				}

				let factor = question.factor ?? "—";
				if (customFactorsEnabled && factor === "Custom") {
					const customFactorId = (question as unknown as { custom_factor?: string | null }).custom_factor;
					factor = (customFactorId && customFactorsMap[customFactorId]) || "Unassigned";
				}

				return {
					questionId,
					factor,
					question: question.question ?? "",
					typeLabel: scaleTypeMap[question.scale] || "N/A",
				};
			})
			.filter(Boolean) as ManualQuestionRow[];
	}, [JSON.stringify(questions), customFactorsEnabled, customFactorsMap]);

	const selectedIdsLookup = useMemo(() => new Set(selectedQuestionIds), [selectedQuestionIds]);

	const filteredRows = useMemo(() => {
		const term = debouncedSearchTerm.toLowerCase().trim();
		return questionRows.filter((row) => {
			const matchesSearch = term ? row.question.toLowerCase().includes(term) : true;
			const matchesSelection = showOnlySelectedQuestions ? selectedIdsLookup.has(row.questionId) : true;
			return matchesSearch && matchesSelection;
		});
	}, [questionRows, debouncedSearchTerm, showOnlySelectedQuestions, selectedIdsLookup]);
	const selectedCount = selectedQuestionIds.length;

	useEffect(() => {
		if (selectedCount === 0) {
			setShowOnlySelectedQuestions(false);
		}
	}, [selectedCount]);

	const columnDefs = useMemo<PYColumnDef<ManualQuestionRow>[]>(() => {
		return [
			{
				headerName: "",
				field: "selection",
				width: 40,
				resizable: false,
				sortable: false,
				cellRenderer: (params) => {
					const questionId = params.data?.questionId;
					if (!questionId) {
						return null;
					}

					const isSelected = selectedIdsLookup.has(questionId);
					return (
						<Checkbox
							id={`manual-question-${questionId}`}
							checked={isSelected}
							onCheckedChange={(checked) => {
								handleCheckboxSelect(questionId, !!checked);
							}}
							disabled={selectedCount >= MAX_SELECTED_QUESTIONS && !isSelected}
						/>
					);
				},
			},
			{
				headerName: "Factor",
				field: "factor",
				sortable: true,
				type: "text",
				minWidth: 200,
				flex: 1,
			},
			{
				headerName: "Question",
				field: "question",
				sortable: true,
				type: "text",
				flex: 3,
				minWidth: 320,
			},
			{
				headerName: "Type",
				field: "typeLabel",
				sortable: true,
				type: "text",
				minWidth: 200,
				flex: 1,
			},
		];
	}, [selectedIdsLookup, selectedCount >= MAX_SELECTED_QUESTIONS]);

	const handleCheckboxSelect = useCallback((questionId: string, checked: boolean) => {
		setSelectedQuestionIds((previous) => {
			if (checked) {
				if (previous.includes(questionId)) {
					return previous;
				}
				if (previous.length >= MAX_SELECTED_QUESTIONS) {
					toast.error(`You can only select up to ${MAX_SELECTED_QUESTIONS} questions.`);
					return previous;
				}
				return [...previous, questionId];
			}
			return previous.filter((id) => id !== questionId);
		});
	}, []);

	const handleConfirmSelection = () => {
		onSelectQuestions(selectedQuestionIds);
		onBack();
	};

	const toggleLabel = (
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
			Show Only Selected Questions{" "}
			<Text overrideElement="span" size="paragraph-3" weight="regular" color="neutral-6">
				({selectedCount}/{MAX_SELECTED_QUESTIONS} Questions)
			</Text>
		</Text>
	);

	return (
		<div
			id="manually-select-questions"
			className="relative mx-auto flex h-full w-full max-w-[880px] flex-col gap-6 bg-neutral-0"
		>
			<div className="mt-8 text-center md:mt-0">
				<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
					Add Selected Questions to Pulse Surveys
				</Text>
				<Text size="paragraph-2" weight="regular" color="neutral-9">
					Choose up to {MAX_SELECTED_QUESTIONS} enabled questions to include in upcoming Pulse Surveys.
				</Text>
				<div
					className={`-mb-2 mt-2 grid text-start transition-all duration-300 ease-in-out ${
						selectedCount >= MAX_SELECTED_QUESTIONS
							? "grid-rows-[1fr] opacity-100"
							: "grid-rows-[0fr] opacity-0"
					}`}
					aria-hidden={selectedCount < MAX_SELECTED_QUESTIONS}
				>
					<div className="overflow-hidden">
						<PageBanner
							size="large"
							color="red"
							mainTitle="You have reached the maximum limit of 32 questions. "
							subTitle="Please remove some existing questions to add new ones."
							icon={faExclamationTriangle}
						/>
					</div>
				</div>
			</div>
			<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
				<div className="w-full md:w-[288px]">
					<InputText
						value={searchTerm}
						onChange={setSearchTerm}
						placeholder="Search questions or factors"
						aria-label="Search questions"
					/>
				</div>
				<div className="w-fit">
					<InputWrapper id="showOnlySelectedQuestions" label={toggleLabel} labelPosition="left">
						<Toggle
							checked={showOnlySelectedQuestions}
							onCheckedChange={(checked) => setShowOnlySelectedQuestions(!!checked)}
							color={showOnlySelectedQuestions ? "primary" : "neutral"}
							size={20}
							showText={false}
							disabled={!selectedCount}
						/>
					</InputWrapper>
				</div>
			</div>
			<div className="h-[480px] min-h-[480px] flex-1">
				<Table<ManualQuestionRow>
					ref={gridRef}
					columnDefs={columnDefs}
					rowData={filteredRows}
					rowSelection="multiple"
					suppressRowClickSelection={true}
					loading={isLoading}
					emptyStateText={
						showOnlySelectedQuestions ? "You have not selected any questions yet." : "No questions found."
					}
				/>
			</div>
			<div className="flex w-full justify-end">
				<div className="w-fit">
					<ButtonStandard color="green" onClick={handleConfirmSelection} disabled={!selectedCount}>
						Complete Selection
					</ButtonStandard>
				</div>
			</div>
			<div className="absolute left-0 top-0">
				<ButtonStandard variant="text" color="neutral" size="small" onClick={onBack} iconLeft={faArrowLeft}>
					Back
				</ButtonStandard>
			</div>
		</div>
	);
};

export default ManuallySelectQuestions;
